import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import format from 'date-fns/format';
import it from 'date-fns/locale/it';
import $ from 'jquery';
import { fetchAllAiCreditsOrders } from '~graphql/controllers/fetchAiCreditsOrders';
import { fetchCreditModules } from '~src/~core/payments';
import { handleBuyAiCredits } from '~src/sidebar/handleBuyAiCreditsNew';
import { initAiCreditPackagesModal } from '~src/sidebar/initAiCreditPackagesModal';
import { initAiCreditsInfoModal } from '~src/sidebar/initAiCreditsInfoModal';
import {
  initAiBlackFridayModal,
  isBlackFridayActive,
} from '~src/sidebar/initAiBlackFridayModal';
import { getUser } from '~src/user';
import { onErrorShowMessage } from '~src/errors';
import {
  buildAiCreditsBalanceForecast,
  buildAiCreditsOrdersWhere,
  formatAiCreditsAmount,
  getAiCreditsForecastRange,
  getSuggestedAiCreditsModule,
} from '~src/utils/aiCreditsHistory';

const formatForecastDate = (date) => {
  return format(date, 'd MMM', { locale: it });
};

const formatCoverageDays = (daysOfCoverage) => {
  if (daysOfCoverage === null) {
    return '';
  }

  if (daysOfCoverage < 1) {
    return 'meno di 1 giorno';
  }

  if (daysOfCoverage < 60) {
    return `${Math.round(daysOfCoverage)} giorni`;
  }

  if (daysOfCoverage < 365) {
    const months = Math.round(daysOfCoverage / 30);
    return `${months} ${months === 1 ? 'mese' : 'mesi'}`;
  }

  const years = Math.round(daysOfCoverage / 365);
  return `${years} ${years === 1 ? 'anno' : 'anni'}`;
};

const getNextCreditDisplay = (loadingForecast, forecast) => {
  if (loadingForecast) {
    return { value: '...', hint: 'Sto calcolando' };
  }

  if (forecast.hasNextCreditEstimate) {
    return {
      value: formatForecastDate(forecast.estimatedNextCreditDate),
      hint: 'In base al tuo piano',
    };
  }

  return { value: 'Da confermare', hint: 'Serve una verifica' };
};

const COVERAGE_COMFORTABLE_DAYS = 180;

const getCoverageDisplay = (loadingForecast, forecast) => {
  if (loadingForecast) {
    return { value: '...', hint: 'Sto calcolando' };
  }

  if (forecast.daysOfCoverage !== null) {
    if (forecast.daysOfCoverage >= COVERAGE_COMFORTABLE_DAYS) {
      return { value: 'Ottima', hint: 'Nessun rischio di esaurimento' };
    }

    return {
      value: formatCoverageDays(forecast.daysOfCoverage),
      hint: 'Stima sul consumo recente',
    };
  }

  return { value: '—', hint: 'Serve più storico' };
};

const getCtaLabel = ({
  isCtaLoading,
  hasStripeCustomer,
  suggestedPurchaseCredits,
  suggestedPurchasePrefix,
  suggestedPurchaseLabel,
}) => {
  if (isCtaLoading) {
    return 'Ricarica crediti';
  }

  if (hasStripeCustomer) {
    if (suggestedPurchaseCredits > 0) {
      return `Ricarica ${suggestedPurchasePrefix}${suggestedPurchaseLabel} crediti`;
    }
    return 'Ricarica crediti';
  }

  if (suggestedPurchaseCredits > 0) {
    return `Richiedi ${suggestedPurchasePrefix}${suggestedPurchaseLabel} crediti`;
  }

  return 'Richiedi una ricarica';
};

const getForecastStatus = (
  forecast,
  suggestedPurchasePrefix,
  suggestedPurchaseLabel
) => {
  if (forecast.enoughUntilNextCredit === false) {
    return {
      tone: 'warning',
      label: `In base al consumo attuale, i crediti potrebbero terminare prima del prossimo accredito. Per evitare interruzioni, ti consigliamo una ricarica di ${suggestedPurchasePrefix}${suggestedPurchaseLabel} crediti.`,
    };
  }

  if (forecast.enoughUntilNextCredit === true) {
    return { tone: 'positive', label: '' };
  }

  if (
    forecast.hasRecentUsage &&
    forecast.daysOfCoverage !== null &&
    forecast.daysOfCoverage < COVERAGE_COMFORTABLE_DAYS
  ) {
    return {
      tone: 'neutral',
      label: `In base al consumo attuale, il saldo dovrebbe coprire circa ${formatCoverageDays(
        forecast.daysOfCoverage
      )}.`,
    };
  }

  return { tone: 'neutral', label: '' };
};

const cleanupModalListeners = () => {
  $(document).off('click.aiCreditPackages', '#credits-info-btn');
  $('#aiCreditPackagesModal').off('.aiCreditPackages');
  $(document).off('click.aiCreditsInfo', '#buy-credits-from-info');
  $('#aiCreditsInfoModal').off('.aiCreditsInfo');
};

const Resoconto = (props) => {
  const [user, setUser] = useState({});
  const [forecastOrders, setForecastOrders] = useState([]);
  const [creditModules, setCreditModules] = useState([]);
  const [loadingForecast, setLoadingForecast] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const forecastRange = getAiCreditsForecastRange();

    initAiCreditPackagesModal();
    initAiCreditsInfoModal();
    if (isBlackFridayActive()) {
      initAiBlackFridayModal();
    }

    Promise.all([
      getUser(),
      fetchAllAiCreditsOrders({
        where: buildAiCreditsOrdersWhere(forecastRange),
      }),
      fetchCreditModules(),
    ])
      .then(([startup, orders, modules]) => {
        if (!isMounted) {
          return;
        }

        setUser(startup.user);
        setForecastOrders(orders);
        setCreditModules(modules || []);
        setLoadingForecast(false);
      })
      .catch((err) => {
        console.error(err);
        if (!isMounted) {
          return;
        }

        setLoadingForecast(false);
        onErrorShowMessage();
      });

    return () => {
      isMounted = false;
      cleanupModalListeners();
    };
  }, []);

  const availableCredits = Number(props.credits?.paidCreditsAmount || 0);
  const forecast = buildAiCreditsBalanceForecast({
    availableCredits,
    org: user.org_pointer,
    orders: forecastOrders,
  });
  const suggestedModule = getSuggestedAiCreditsModule(
    creditModules,
    forecast.suggestedPurchaseCredits
  );
  const suggestedPurchaseCredits = suggestedModule
    ? suggestedModule.included_quantity
    : forecast.suggestedPurchaseCredits;
  const suggestedPurchaseLabel =
    suggestedPurchaseCredits > 0
      ? formatAiCreditsAmount(suggestedPurchaseCredits)
      : '';
  const suggestedPurchasePrefix = suggestedModule ? '' : 'circa ';
  const hasStripeCustomer = Boolean(user.org_pointer?.stripe_customer_id);
  const isCtaLoading = loadingForecast;
  const isManualCreditRequest = !isCtaLoading && !hasStripeCustomer;
  const nextCredit = getNextCreditDisplay(loadingForecast, forecast);
  const coverage = getCoverageDisplay(loadingForecast, forecast);
  const ctaLabel = getCtaLabel({
    isCtaLoading,
    hasStripeCustomer,
    suggestedPurchaseCredits,
    suggestedPurchasePrefix,
    suggestedPurchaseLabel,
  });
  const forecastStatus = getForecastStatus(
    forecast,
    suggestedPurchasePrefix,
    suggestedPurchaseLabel
  );
  const buyModalOptions = suggestedModule
    ? {
        preselectedModuleId: suggestedModule.objectId,
      }
    : {};

  return (
    <section className="ai-credits-history__summary">
      <div className="ai-credits-history__summary-header">
        <div className="ai-credits-history__summary-grid">
          <article className="ai-credits-history__summary-card ai-credits-history__summary-card--primary">
            <span className="ai-credits-history__summary-card-label">
              Saldo disponibile
            </span>
            <div className="ai-credits-history__summary-metric">
              <strong>{availableCredits}</strong>
              <small>crediti</small>
            </div>
          </article>

          <article className="ai-credits-history__summary-card">
            <span className="ai-credits-history__summary-card-label">
              Prossimo accredito
            </span>
            <strong className="ai-credits-history__summary-card-value">
              {nextCredit.value}
            </strong>
            <small className="ai-credits-history__summary-card-note">
              {nextCredit.hint}
            </small>
          </article>

          <article className="ai-credits-history__summary-card">
            <span className="ai-credits-history__summary-card-label">
              Autonomia stimata
            </span>
            <strong className="ai-credits-history__summary-card-value">
              {coverage.value}
            </strong>
            <small className="ai-credits-history__summary-card-note">
              {coverage.hint}
            </small>
          </article>
        </div>

        <div className="ai-credits-history__summary-actions">
          {!isManualCreditRequest ? (
            <Button
              variant="primary"
              type="submit"
              className="ai-credits-history__summary-cta requestBgCredits"
              disabled={isCtaLoading}
              onClick={() => handleBuyAiCredits(buyModalOptions)}
            >
              {ctaLabel}
            </Button>
          ) : (
            <button
              type="button"
              className="btn btn-primary ai-credits-history__summary-cta open_chat_button"
            >
              <span>{ctaLabel}</span>
            </button>
          )}

          <div className="ai-credits-history__summary-links">
            <a
              href="https://wiki.onoranzefunebricloud.com/guide/crediti-intelligenza-artificiale"
              target="_blank"
              rel="noreferrer"
              className="ai-credits-history__summary-link"
            >
              Guida crediti AI
            </a>
          </div>
        </div>
      </div>

      {!loadingForecast && forecastStatus.label ? (
        <div
          className={`ai-credits-history__summary-status ai-credits-history__summary-status--${forecastStatus.tone}`}
        >
          <p className="mb-0">{forecastStatus.label}</p>
        </div>
      ) : null}

      {availableCredits === 0 && (
        <p className="ai-credits-history__summary-empty mb-0">
          Saldo esaurito. Ricarica per continuare a usare gli strumenti AI.
        </p>
      )}
    </section>
  );
};

Resoconto.propTypes = {
  credits: PropTypes.object,
};

export default Resoconto;
